import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  DrawingManager,
  GoogleMap,
  Marker,
  OverlayView,
  Polygon,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const BUBBLE_WIDTH = 300;
const ARROW_PX = 6;
const DEFAULT_ANCHOR_Y = 40;
const containerStyle = { width: "100%", height: "100%" };

const MarkerBubbleFixed = ({ position, title, anchorY = DEFAULT_ANCHOR_Y }) => (
  <>
    <Marker position={position} />
    <OverlayView
      position={position}
      mapPaneName={OverlayView.FLOAT_PANE}
      getPixelPositionOffset={() => ({
        x: -Math.round(BUBBLE_WIDTH / 2),
        y: 0,
      })}
    >
      <div
        style={{
          width: BUBBLE_WIDTH,
          transform: `translateY(calc(-100% - ${anchorY + ARROW_PX}px))`,
          background: "rgba(0,0,0,0.95)",
          color: "#fff",
          borderRadius: 10,
          padding: "8px 12px",
          fontSize: 12,
          lineHeight: 1.3,
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          pointerEvents: "none",
          boxShadow: "0 6px 16px rgba(0,0,0,.25)",
          position: "relative",
          textAlign: "center",
          fontVariantNumeric: "lining-nums",
        }}
      >
        {title && <div style={{ fontWeight: 600 }}>{title}</div>}
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: -ARROW_PX,
            width: 0,
            height: 0,
            borderLeft: `${ARROW_PX}px solid transparent`,
            borderRight: `${ARROW_PX}px solid transparent`,
            borderTop: `${ARROW_PX}px solid rgba(0,0,0,0.95)`,
          }}
        />
      </div>
    </OverlayView>
  </>
);

const PolygonEditorMap = ({
  maxVertices = 15,
  center,
  paths,
  setPaths,
  drawingMode,
  setDrawingMode,
  tempIsInside,
  onInsideChange,
  addressData,
}) => {
  const libraries = useMemo(() => ["drawing", "geometry"], []);
  const { mapType } = useSelector((state) => state.layout);
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script-polygon-limit",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
    version: "3.64",
  });

  const mapRef = useRef(null);
  const polygonRef = useRef(null);
  const listenersRef = useRef([]);
  const drawingClickListenerRef = useRef(null);
  const domPointerListenerRef = useRef(null);
  const domPointerCountRef = useRef(0);
  const warnedRef = useRef(false);

  useEffect(() => {
    if (
      !window.google?.maps?.geometry ||
      !Array.isArray(paths) ||
      paths.length < 3 ||
      !center
    ) {
      onInsideChange?.(false);
      return;
    }

    const polygon = new window.google.maps.Polygon({ paths });
    const centerPoint = new window.google.maps.LatLng(center.lat, center.lng);
    onInsideChange?.(
      window.google.maps.geometry.poly.containsLocation(centerPoint, polygon)
    );
  }, [paths, center, onInsideChange]);

  const onEdit = useCallback(() => {
    const polygon = polygonRef.current;
    if (!polygon) return;
    const path = polygon.getPath();

    if (path.getLength() > maxVertices) {
      if (!warnedRef.current) {
        warnedRef.current = true;
        toast.error(`En fazla ${maxVertices} adet poligon noktası girilebilir.`);
        setTimeout(() => {
          warnedRef.current = false;
        }, 600);
      }
      path.removeAt(path.getLength() - 1);
      return;
    }

    const next = [];
    for (let index = 0; index < path.getLength(); index += 1) {
      const point = path.getAt(index);
      next.push({ lat: point.lat(), lng: point.lng() });
    }
    setPaths(next);
  }, [maxVertices, setPaths]);

  const onLoadPolygon = useCallback(
    (polygon) => {
      polygonRef.current = polygon;
      listenersRef.current.forEach((listener) => listener?.remove?.());
      listenersRef.current = [];
      onEdit();

      const path = polygon.getPath();
      listenersRef.current.push(
        path.addListener("insert_at", onEdit),
        path.addListener("set_at", onEdit),
        path.addListener("remove_at", onEdit)
      );
    },
    [onEdit]
  );

  const onUnmountPolygon = useCallback(() => {
    listenersRef.current.forEach((listener) => listener?.remove?.());
    listenersRef.current = [];
    polygonRef.current = null;
  }, []);

  const handleMapLoad = (map) => {
    mapRef.current = map;
    if (paths.length > 0 && window.google?.maps?.LatLngBounds) {
      const bounds = new window.google.maps.LatLngBounds();
      paths.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds, 48);
    }
  };

  const detachDrawingClickLimiter = useCallback(() => {
    if (drawingClickListenerRef.current) {
      window.google?.maps?.event?.removeListener(drawingClickListenerRef.current);
      drawingClickListenerRef.current = null;
    }
  }, []);

  const detachVertexCapDOM = useCallback(() => {
    const mapDiv = mapRef.current?.getDiv?.();
    if (mapDiv && domPointerListenerRef.current) {
      mapDiv.removeEventListener("pointerdown", domPointerListenerRef.current);
      domPointerListenerRef.current = null;
    }
    domPointerCountRef.current = 0;
  }, []);

  const attachVertexCapDOM = () => {
    const map = mapRef.current;
    const mapDiv = map?.getDiv?.();
    if (!map || !mapDiv) return;

    detachVertexCapDOM();
    const handler = () => {
      domPointerCountRef.current += 1;
      if (domPointerCountRef.current >= maxVertices) {
        toast.error(`En fazla ${maxVertices} adet poligon noktası girilebilir.`);
        try {
          window.google?.maps?.event?.trigger(map, "dblclick");
        } catch (_) {}
        setDrawingMode(null);
        detachVertexCapDOM();
      }
    };

    mapDiv.addEventListener("pointerdown", handler, { passive: true });
    domPointerListenerRef.current = handler;
  };

  useEffect(() => {
    if (drawingMode !== "polygon") detachDrawingClickLimiter();
    return () => {
      detachDrawingClickLimiter();
      detachVertexCapDOM();
    };
  }, [drawingMode, detachDrawingClickLimiter, detachVertexCapDOM]);

  const startDrawing = () => {
    setPaths([]);
    setDrawingMode("polygon");
    attachVertexCapDOM();
  };

  const resetAll = () => {
    setPaths([]);
    setDrawingMode(null);
    detachDrawingClickLimiter();
    detachVertexCapDOM();
  };

  if (!isLoaded) return null;

  return (
    <GoogleMap
      onLoad={handleMapLoad}
      mapTypeId={mapType}
      mapContainerStyle={containerStyle}
      center={center}
      zoom={10}
    >
      <div
        style={{
          position: "absolute",
          bottom: 16,
          left: 16,
          display: "flex",
          gap: 8,
          zIndex: 2,
        }}
      >
        {!drawingMode && (
          <button
            type="button"
            onClick={startDrawing}
            style={{
              fontSize: 14,
              fontWeight: 600,
              padding: "8px 12px",
              borderRadius: 8,
              color: "#fff",
              background: "var(--color-main)",
              cursor: "pointer",
            }}
          >
            Çizime Başla
          </button>
        )}
        <button
          type="button"
          onClick={resetAll}
          style={{
            fontSize: 14,
            fontWeight: 600,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ccc",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Sıfırla
        </button>
      </div>

      {drawingMode === "polygon" && (
        <div
          style={{
            position: "absolute",
            top: 64,
            left: 16,
            padding: 10,
            borderRadius: 8,
            background: "white",
            boxShadow: "0 6px 16px rgba(0,0,0,.15)",
            zIndex: 2,
            maxWidth: 320,
            fontSize: 12,
          }}
        >
          <b>Çizim modu aktif</b>
          <div>- Haritaya tıklayarak köşeleri ekle</div>
          <div>- {maxVertices}. noktada otomatik bitirilecek</div>
          <div>- Çift tıklayarak direkt bitirebilirsin</div>
          <div>- Oluşturduğun poligonu sürükleyebilirsin</div>
        </div>
      )}

      <Marker position={center} />

      {window.google?.maps?.drawing && (
        <DrawingManager
          options={{
            drawingMode,
            drawingControl: false,
            polygonOptions: {
              fillColor: "#4285F4",
              fillOpacity: 0.2,
              strokeColor: "#4285F4",
              strokeOpacity: 0.9,
              strokeWeight: 2,
              editable: true,
              draggable: true,
              clickable: true,
              zIndex: 1,
            },
          }}
          onOverlayComplete={(event) => {
            if (event.type !== window.google.maps.drawing.OverlayType.POLYGON) return;

            const drawnPolygon = event.overlay;
            const path = drawnPolygon.getPath();
            const points = [];
            for (let index = 0; index < path.getLength(); index += 1) {
              const point = path.getAt(index);
              points.push({ lat: point.lat(), lng: point.lng() });
            }

            setPaths(points.slice(0, maxVertices));
            detachVertexCapDOM();
            setDrawingMode(null);
            drawnPolygon.setMap(null);
          }}
        />
      )}

      {paths.length > 0 && (
        <Polygon
          paths={paths}
          options={{
            fillColor: tempIsInside ? "#4285F4" : "red",
            strokeColor: tempIsInside ? "#4285F4" : "red",
            fillOpacity: 0.2,
            strokeOpacity: 0.9,
            strokeWeight: 2,
            editable: true,
            draggable: true,
            clickable: true,
            zIndex: 1,
          }}
          onLoad={onLoadPolygon}
          onUnmount={onUnmountPolygon}
          onMouseUp={onEdit}
          onDragEnd={onEdit}
        />
      )}

      {!drawingMode && paths.length < 3 && (
        <MarkerBubbleFixed position={center} title={addressData} anchorY={40} />
      )}
    </GoogleMap>
  );
};

export default PolygonEditorMap;
